/**
 * Description du fichier:
 * Service central pour tous les calculs liés aux dimensions, surfaces, coutures et prix des articles.
 * 
 * Relations avec d'autres fichiers:
 * - Utilise les types Dimensions, Article et Piece définis dans ../types/dimensions.ts
 * - Utilise le type Matiere défini dans ../pages/Devis/ConfigurateurArticle/types.ts
 * - Utilisé par CalepinageService pour convertir les articles en pièces
 * - Utilisé par DevisService pour calculer les prix des articles
 * 
 * Actions déclenchées:
 * - Calcule les dimensions avec talon pour différentes formes d'articles (rectangulaire, rond, cylindre)
 * - Calcule les surfaces des différentes parties des articles (dessus, fond, bandeau/joues)
 * - Calcule les longueurs de couture selon des formules spécifiques à chaque forme
 * - Calcule le temps de main d'œuvre nécessaire à la fabrication
 * - Calcule les coûts des matières et accessoires
 * - Convertit les articles en pièces pour le calepinage
 */

import { Dimensions, Article, Piece } from '../types/dimensions';
import { Matiere } from '../pages/Devis/ConfigurateurArticle/types';

export class DimensionsService {
  /**
   * Calcule toutes les dimensions avec talon pour un article
   */
  static calculerDimensionsAvecTalon(dimensions: Dimensions): Dimensions {
    const result = { ...dimensions };
    const talon = dimensions.talon || 0;
    
    if (!result.avecTalon) result.avecTalon = {};
    
    // SUPPRESSION de la valeur par défaut pour la propriété forme
    // Afficher la forme pour le débogage


    // Vérifier si forme existe avant d'appeler includes
    if (dimensions.forme && typeof dimensions.forme === 'string') {
      if (dimensions.forme.includes('rectangulaire')) {
        if (dimensions.longueur && dimensions.largeur) {
          result.avecTalon.longueur = dimensions.longueur + 2 * talon;
          result.avecTalon.largeur = dimensions.largeur + 2 * talon;
          if (dimensions.hauteur) {
            result.avecTalon.hauteur = dimensions.hauteur + 2 * talon;
            result.avecTalon.perimetre = 2 * (result.avecTalon.longueur + result.avecTalon.largeur);
          }
        }
      } else if (dimensions.forme.includes('rond') || dimensions.forme.includes('cylindre')) {
        if (dimensions.diametre) {
          result.avecTalon.diametre = dimensions.diametre + 2 * talon;
          if (dimensions.hauteur) {
            result.avecTalon.hauteur = dimensions.hauteur + 2 * talon;
            result.avecTalon.perimetre = Math.PI * result.avecTalon.diametre;
          }
        }
      } else {
        console.warn("DimensionsService - calculerDimensionsAvecTalon - forme non reconnue:", dimensions.forme);
      }
    } else {
      // Si forme n'est pas définie, afficher un avertissement
      console.error("ERREUR: La propriété 'forme' n'est pas définie pour cet article. Impossible de calculer les dimensions avec talon correctement.");
    }
    
    return result;
  }
  
  /**
   * Calcule toutes les surfaces pour un article
   */
  static calculerSurfaces(dimensions: Dimensions): { dessus: number, fond: number, bandeau: number, total: number } {
    const result = {
      dessus: 0,
      fond: 0,
      bandeau: 0,
      total: 0
    };
    
    const talon = dimensions.talon || 0;
    const avecTalon = dimensions.avecTalon || this.calculerDimensionsAvecTalon(dimensions).avecTalon || {};
    
    // SUPPRESSION de la valeur par défaut pour la propriété forme
    // Afficher la forme pour le débogage


    // Calcul des surfaces en fonction de la forme
    if (dimensions.forme && typeof dimensions.forme === 'string') {
      if (dimensions.forme.includes('rectangulaire')) {
        // Rectangulaire avec bandeau ou joues
        if (avecTalon.longueur && avecTalon.largeur) {
          // Surface du dessus
          result.dessus = (avecTalon.longueur * avecTalon.largeur) / 10000; // Convertir en m²
          
          // Surface du fond (si option fond)
          result.fond = result.dessus;
          
          // Surface du bandeau ou des joues
          if (avecTalon.hauteur) {
            if (dimensions.forme === 'rectangulaire_bandeau' && avecTalon.perimetre !== undefined && avecTalon.hauteur !== undefined) {
              // Bandeau: périmètre × hauteur
              result.bandeau = (avecTalon.perimetre * avecTalon.hauteur) / 10000;
            } else if (dimensions.forme === 'rectangulaire_joues' && avecTalon.hauteur !== undefined && avecTalon.largeur !== undefined) {
              // Joues: 2 × hauteur × largeur
              result.bandeau = (2 * avecTalon.hauteur * avecTalon.largeur) / 10000;
            } else {
              console.warn("DimensionsService - calculerSurfaces - forme rectangulaire non spécifiée:", dimensions.forme);
            }
          }
        }
      } else if (dimensions.forme === 'rond_bandeau') {
        // Rond avec bandeau
        if (avecTalon.diametre) {
          // Surface du dessus (cercle)
          const rayon = avecTalon.diametre / 2;
          result.dessus = (Math.PI * rayon * rayon) / 10000;
          
          // Surface du fond (si option fond)
          result.fond = result.dessus;
          
          // Surface du bandeau
          if (avecTalon.hauteur && avecTalon.perimetre) {
            result.bandeau = (avecTalon.perimetre * avecTalon.hauteur) / 10000;
          }
        }
      } else if (dimensions.forme === 'cylindre_joues') {
        // Cylindre avec joues
        if (avecTalon.diametre) {
          // Surface des joues (2 cercles)
          const rayon = avecTalon.diametre / 2;
          result.dessus = (2 * Math.PI * rayon * rayon) / 10000;
          
          // Surface du cylindre
          if (avecTalon.hauteur && avecTalon.perimetre && dimensions.longueur) {
            const longueurAvecTalon = dimensions.longueur + 2 * talon;
            result.bandeau = (avecTalon.perimetre * longueurAvecTalon) / 10000;
          }
        }
      } else {
        console.warn("DimensionsService - calculerSurfaces - forme non reconnue:", dimensions.forme);
      }
    } else {
      // Si forme n'est pas définie, afficher un avertissement
      console.error("ERREUR: La propriété 'forme' n'est pas définie pour cet article. Impossible de calculer les surfaces correctement.");
    }
    
    // Calculer la surface totale
    result.total = result.dessus + result.fond + result.bandeau;
    
    return result;
  }
  
  /**
   * Calcule toutes les longueurs de couture pour un article
   * 
   * FORMULES CORRECTES POUR LES LONGUEURS DE COUTURES (pour la tarification) :
   * 
   * 1. Rectangle avec Joues:
   *    - Dessus: 0 (aucune couture car comptabilisées sur les joues)
   *    - Joue 1: H + l + H (hauteur + largeur + hauteur)
   *    - Joue 2: H + l + H (hauteur + largeur + hauteur)
   *    - Fond: 2×L + 2×l (si présence de l'option Fond)
   * 
   * 2. Rectangle avec Bandeau:
   *    - Dessus: 2×L + 2×l (périmètre du dessus)
   *    - Bandeau: H (uniquement la jonction verticale)
   *    - Fond: 2×L + 2×l (si présence de l'option Fond)
   * 
   * 3. Rond avec Bandeau:
   *    - Dessus: Périmètre (circonférence du cercle)
   *    - Bandeau: H (uniquement la jonction verticale)
   *    - Fond: Périmètre (si présence de l'option Fond)
   * 
   * 4. Cylindre avec Joues:
   *    - Joue 1: Périmètre (circonférence du cercle)
   *    - Joue 2: Périmètre (circonférence du cercle)
   *    - Cylindre: L (la jonction des deux bouts du corps du cylindre)
   *    - Pas de notion de fond pour cette forme
   */
  static calculerCoutures(dimensions: Dimensions): { dessus: number, fond: number, jonction: number, total: number } {
    const result = {
      dessus: 0,
      fond: 0,
      jonction: 0,
      total: 0
    };
    
    const avecTalon = dimensions.avecTalon || this.calculerDimensionsAvecTalon(dimensions).avecTalon || {};
    
    // SUPPRESSION de la valeur par défaut pour la propriété forme
    // Afficher la forme pour le débogage


    // Calcul des coutures en fonction de la forme
    if (dimensions.forme && typeof dimensions.forme === 'string') {
      if (dimensions.forme.includes('rectangulaire')) {
        if (avecTalon.longueur && avecTalon.largeur) {
          if (dimensions.forme === 'rectangulaire_bandeau') {
            // Rectangle avec Bandeau
            if (avecTalon.hauteur) {
              // Dessus: 2×L + 2×l (périmètre du dessus)
              result.dessus = 2 * (avecTalon.longueur + avecTalon.largeur) / 100; // Convertir en m
              
              // Fond: 2×L + 2×l (si présence de l'option Fond)
              result.fond = result.dessus;
              
              // Bandeau: H (uniquement la jonction verticale)
              result.jonction = avecTalon.hauteur / 100; // Hauteur du bandeau en m
              
              // Pour le calcul total, utiliser la formule 4L + 4l + H (avec fond)
              // ou 2L + 2l + H (sans fond)
              const longueur = dimensions.longueur || 0;
              const largeur = dimensions.largeur || 0;
              const hauteur = dimensions.hauteur || 0;
              
              // Utiliser la formule 4L + 4l + H pour le total avec fond
              // Sinon, utiliser la somme des coutures individuelles (dessus + jonction)
              result.total = result.dessus + result.jonction;
              
              // Si on a un fond, on utilise la formule spécifique
              if (result.fond > 0) {
                result.total = (4 * longueur + 4 * largeur + hauteur) / 100;
              }



              console.log("  - Jonction (Bandeau):", result.jonction);

              // Retourner directement pour éviter le recalcul du total à la fin
              return result;
            }
          } else if (dimensions.forme === 'rectangulaire_joues') {
            // Rectangle avec Joues
            if (avecTalon.hauteur) {
              // Dessus: 0 (aucune couture car comptabilisées sur les joues)
              result.dessus = 0;
              
              // Fond: 2×L + 2×l (si présence de l'option Fond)
              result.fond = 2 * (avecTalon.longueur + avecTalon.largeur) / 100;
              
              // Joues: 2 × (H + l + H) = 2 × (2×H + l)
              result.jonction = 2 * (2 * avecTalon.hauteur + avecTalon.largeur) / 100;



              console.log("  - Jonction (Joues):", result.jonction);
            }
          } else {
            console.warn("DimensionsService - calculerCoutures - forme rectangulaire non spécifiée:", dimensions.forme);
          }
        }
      } else if (dimensions.forme === 'rond_bandeau') {
        // Rond avec Bandeau
        if (avecTalon.diametre && avecTalon.perimetre) {
          // Dessus: Périmètre (circonférence du cercle)
          result.dessus = avecTalon.perimetre / 100;
          
          // Fond: Périmètre (si présence de l'option Fond)
          result.fond = result.dessus;
          
          // Bandeau: H (uniquement la jonction verticale)
          if (avecTalon.hauteur) {
            result.jonction = avecTalon.hauteur / 100; // Hauteur du bandeau en m
          }



          console.log("  - Jonction (Bandeau):", result.jonction);
        }
      } else if (dimensions.forme === 'cylindre_joues') {
        // Cylindre avec Joues
        if (avecTalon.diametre && avecTalon.perimetre) {
          // Joue 1: Périmètre (circonférence du cercle)
          result.dessus = avecTalon.perimetre / 100;
          
          // Joue 2: Périmètre (circonférence du cercle)
          result.fond = avecTalon.perimetre / 100;
          
          // Cylindre: L (la jonction des deux bouts du corps du cylindre)
          if (dimensions.longueur) {
            const longueurAvecTalon = dimensions.longueur + 2 * (dimensions.talon || 0);
            result.jonction = longueurAvecTalon / 100; // Longueur du cylindre en m
          }



          console.log("  - Jonction (Cylindre):", result.jonction);
        }
      } else {
        console.warn("DimensionsService - calculerCoutures - forme non reconnue:", dimensions.forme);
      }
    } else {
      // Si forme n'est pas définie, afficher un avertissement
      console.error("ERREUR: La propriété 'forme' n'est pas définie pour cet article. Impossible de calculer les coutures correctement.");
    }
    
    // Calculer la longueur totale de couture
    result.total = result.dessus + result.fond + result.jonction;
    
    return result;
  }
  
  /**
   * Calcule le temps de main d'œuvre pour un article
   */
  static calculerTempsMainOeuvre(dimensions: Dimensions, options: { fond: boolean, fermeture?: string }): { couture: number, gabarit: number, poseFermeture: number, autres: number, total: number, totalHeures: number } {
    const result = {
      couture: 0,
      gabarit: 0,
      poseFermeture: 0,
      autres: 0,
      total: 0,
      totalHeures: 0
    };
    
    // Calculer les coutures si elles ne sont pas déjà calculées
    const coutures = dimensions.coutures || this.calculerCoutures(dimensions);
    
    // Temps de couture: 5 minutes par mètre de couture
    result.couture = coutures.total * 5;
    
    // Temps de gabarit: 10 minutes par article
    result.gabarit = 10;
    
    // Temps de pose de fermeture
    if (options.fermeture) {
      result.poseFermeture = 15; // 15 minutes pour poser une fermeture
    }
    
    // Autres temps (préparation, finition, etc.)
    result.autres = 10;
    
    // Temps total en minutes
    result.total = result.couture + result.gabarit + result.poseFermeture + result.autres;
    
    // Temps total en heures
    result.totalHeures = result.total / 60;
    
    return result;
  }
  
  /**
   * Calcule le coût des matières pour un article
   * @param dimensions Les dimensions de l'article
   * @param materiaux Les matériaux utilisés pour l'article
   * @param matieres La liste des matières disponibles
   * @param isPremium Indique si l'utilisateur a un abonnement Premium
   * @returns Le coût total des matières
   */
  static calculerCoutMatieres(
    dimensions: Dimensions, 
    materiaux: { 
      dessus: { matiere: string, couleur: string, enStock?: boolean }, 
      dessous: { matiere: string, couleur: string, enStock?: boolean }, 
      cotes: { matiere: string, couleur: string, enStock?: boolean } 
    }, 
    matieres: Matiere[],
    isPremium: boolean = false
  ): number {
    // Calculer les surfaces si elles ne sont pas déjà calculées
    const surfaces = dimensions.surfaces || this.calculerSurfaces(dimensions);
    
    // Trouver les matières correspondantes
    const matiereDessus = matieres.find(m => m.id === materiaux.dessus.matiere);
    const matiereDessous = matieres.find(m => m.id === materiaux.dessous.matiere);
    const matiereCotes = matieres.find(m => m.id === materiaux.cotes.matiere);
    
    if (!matiereDessus || !matiereDessous || !matiereCotes) {
      console.error("Matière non trouvée pour l'article");
      return 0;
    }
    
    // Calculer le coût des matières
    let coutDessus = 0;
    let coutFond = 0;
    let coutBandeau = 0;
    
      // Calculer le coût du dessus
      if (materiaux.dessus.enStock) {
        // Si la matière est en stock, utiliser la méthode actuelle (surface × prix)
        coutDessus = surfaces.dessus * matiereDessus.prix;
      } else {
        // Si la matière n'est pas en stock, utiliser le calepinage
        // Convertir l'article en pièces pour le calepinage
        const pieces = this.articleEnPieces({
          id: 'temp',
          nom: 'temp',
          forme: dimensions.forme || 'rectangulaire_bandeau',
          dimensions,
          materiaux,
          options: { fond: true },
          quantite: 1
        } as any);
        
        // Filtrer les pièces pour ne garder que celles du dessus
        const piecesDessus = pieces.filter(p => p.type === 'dessus' && p.matiere === materiaux.dessus.matiere);
        
        if (piecesDessus.length > 0) {
          // Calculer la longueur de matière nécessaire pour le dessus
          const prixMetreLineaire = matiereDessus.prix; // Prix au mètre linéaire (déjà en €/ML)
          
          // Utiliser la longueur calculée par le calepinage
          const longueurNecessaire = piecesDessus[0].width / 100; // Convertir en mètres
          
          // Calculer le coût
          coutDessus = longueurNecessaire * prixMetreLineaire;
          
          // Appliquer une majoration de 20% pour les utilisateurs Standard
          if (!isPremium) {
            coutDessus *= 1.2;
          }
        } else {
          // Si aucune pièce n'est trouvée, utiliser la méthode actuelle
          coutDessus = surfaces.dessus * matiereDessus.prix;
        }
      }
    
      // Calculer le coût du fond
      if (materiaux.dessous.enStock) {
        // Si la matière est en stock, utiliser la méthode actuelle (surface × prix)
        coutFond = surfaces.fond * matiereDessous.prix;
      } else {
        // Si la matière n'est pas en stock, utiliser le calepinage
        // Convertir l'article en pièces pour le calepinage
        const pieces = this.articleEnPieces({
          id: 'temp',
          nom: 'temp',
          forme: dimensions.forme || 'rectangulaire_bandeau',
          dimensions,
          materiaux,
          options: { fond: true },
          quantite: 1
        } as any);
        
        // Filtrer les pièces pour ne garder que celles du fond
        const piecesFond = pieces.filter(p => p.type === 'fond' && p.matiere === materiaux.dessous.matiere);
        
        if (piecesFond.length > 0) {
          // Calculer la longueur de matière nécessaire pour le fond
          const prixMetreLineaire = matiereDessous.prix; // Prix au mètre linéaire (déjà en €/ML)
          
          // Utiliser la longueur calculée par le calepinage
          const longueurNecessaire = piecesFond[0].width / 100; // Convertir en mètres
          
          // Calculer le coût
          coutFond = longueurNecessaire * prixMetreLineaire;
          
          // Appliquer une majoration de 20% pour les utilisateurs Standard
          if (!isPremium) {
            coutFond *= 1.2;
          }
        } else {
          // Si aucune pièce n'est trouvée, utiliser la méthode actuelle
          coutFond = surfaces.fond * matiereDessous.prix;
        }
      }
    
      // Calculer le coût du bandeau/côtés
      if (materiaux.cotes.enStock) {
        // Si la matière est en stock, utiliser la méthode actuelle (surface × prix)
        coutBandeau = surfaces.bandeau * matiereCotes.prix;
      } else {
        // Si la matière n'est pas en stock, utiliser le calepinage
        // Convertir l'article en pièces pour le calepinage
        const pieces = this.articleEnPieces({
          id: 'temp',
          nom: 'temp',
          forme: dimensions.forme || 'rectangulaire_bandeau',
          dimensions,
          materiaux,
          options: { fond: true },
          quantite: 1
        } as any);
        
        // Filtrer les pièces pour ne garder que celles du bandeau/côtés
        const piecesBandeau = pieces.filter(p => 
          (p.type === 'bandeau' || p.type === 'joue1' || p.type === 'joue2' || p.type === 'cylindre') && 
          p.matiere === materiaux.cotes.matiere
        );
        
        if (piecesBandeau.length > 0) {
          // Calculer la longueur de matière nécessaire pour le bandeau/côtés
          const prixMetreLineaire = matiereCotes.prix; // Prix au mètre linéaire (déjà en €/ML)
          
          // Calculer la longueur totale nécessaire
          let longueurTotale = 0;
          for (const piece of piecesBandeau) {
            longueurTotale += piece.width / 100; // Convertir en mètres
          }
          
          // Calculer le coût
          coutBandeau = longueurTotale * prixMetreLineaire;
          
          // Appliquer une majoration de 20% pour les utilisateurs Standard
          if (!isPremium) {
            coutBandeau *= 1.2;
          }
        } else {
          // Si aucune pièce n'est trouvée, utiliser la méthode actuelle
          coutBandeau = surfaces.bandeau * matiereCotes.prix;
        }
      }
    
    return coutDessus + coutFond + coutBandeau;
  }
  
  /**
   * Calcule le coût des accessoires pour un article
   */
  static calculerCoutAccessoires(options: { fond: boolean, fermeture?: string, mousse?: string }, dimensions: Dimensions): { cout: number, volumeMousse?: number } {
    const result = {
      cout: 0,
      volumeMousse: 0
    };
    
    // Coût de la fermeture
    if (options.fermeture) {
      // Prix forfaitaire pour la fermeture
      result.cout += 5;
    }
    
    // Coût de la mousse
    if (options.mousse) {
      // Calculer le volume de mousse en m³
      const surfaces = dimensions.surfaces || this.calculerSurfaces(dimensions);
      const hauteur = dimensions.hauteur || 0;
      
      // Volume = surface du dessus × hauteur
      result.volumeMousse = surfaces.dessus * (hauteur / 100); // Convertir hauteur en m
      
      // Prix de la mousse au m³
      const prixMousse = options.mousse === 'souple' ? 50 : 80; // 50€/m³ pour mousse souple, 80€/m³ pour mousse ferme
      
      result.cout += result.volumeMousse * prixMousse;
    }
    
    return result;
  }
  
  /**
   * Calcule le prix total d'un article
   * @param article L'article à calculer
   * @param matieres La liste des matières disponibles
   * @param tauxHoraire Le taux horaire pour la main d'œuvre
   * @param isPremium Indique si l'utilisateur a un abonnement Premium
   * @returns Le prix calculé et les détails du calcul
   */
  static calculerPrixArticle(article: Article, matieres: Matiere[], tauxHoraire: number, isPremium: boolean = false): { prix: number, details: any } {
    // Compléter les dimensions avec les calculs
    // Calculer les dimensions avec talon
    const dimensionsAvecTalon = this.calculerDimensionsAvecTalon(article.dimensions);
    
    // S'assurer que avecTalon existe
    if (!dimensionsAvecTalon.avecTalon) {
      dimensionsAvecTalon.avecTalon = {};
    }
    
    // Compléter les dimensions
    const dimensionsCompletes = {
      ...article.dimensions,
      avecTalon: dimensionsAvecTalon.avecTalon,
      surfaces: this.calculerSurfaces(article.dimensions),
      coutures: this.calculerCoutures(article.dimensions)
    };
    
    // Calculer le temps de main d'œuvre
    const temps = this.calculerTempsMainOeuvre(dimensionsCompletes, article.options);
    
    // Calculer le coût des matières en tenant compte de l'option "Matière en stock" et du type d'abonnement
    const coutMatieres = this.calculerCoutMatieres(dimensionsCompletes, article.materiaux, matieres, isPremium);
    
    // Calculer le coût des accessoires
    const accessoires = this.calculerCoutAccessoires(article.options, dimensionsCompletes);
    
    // Calculer le coût de la main d'œuvre
    const coutMainOeuvre = temps.totalHeures * tauxHoraire;
    
    // Calculer le coût total
    const coutTotal = coutMainOeuvre + coutMatieres + accessoires.cout;
    
    // Calculer le prix de vente (avec marge)
    const marge = 1.3; // 30% de marge
    const prix = coutTotal * marge;
    
    // Informations sur le calepinage
    const calepinageInfo = {
      utilisationCalepinage: false,
      matieresEnStock: {
        dessus: article.materiaux.dessus.enStock === true,
        dessous: article.materiaux.dessous.enStock === true,
        cotes: article.materiaux.cotes.enStock === true
      },
      majorationStandard: !isPremium,
      longueursMatieres: {
        dessus: 0,
        dessous: 0,
        cotes: 0
      }
    };
    
    // Vérifier si le calepinage a été utilisé pour au moins une matière
    if (!article.materiaux.dessus.enStock || !article.materiaux.dessous.enStock || !article.materiaux.cotes.enStock) {
      calepinageInfo.utilisationCalepinage = true;
      
      // Calculer les longueurs de matière nécessaires pour chaque face
      const pieces = this.articleEnPieces({
        id: 'temp',
        nom: 'temp',
        forme: article.dimensions.forme || 'rectangulaire_bandeau',
        dimensions: article.dimensions,
        materiaux: article.materiaux,
        options: { fond: true },
        quantite: 1
      } as any);
      
      // Calculer la longueur pour le dessus
      if (!article.materiaux.dessus.enStock) {
        const piecesDessus = pieces.filter(p => p.type === 'dessus' && p.matiere === article.materiaux.dessus.matiere);
        if (piecesDessus.length > 0) {
          const matiereDessus = matieres.find(m => m.id === article.materiaux.dessus.matiere);
          if (matiereDessus) {
            const longueurNecessaire = piecesDessus[0].width / 100; // Convertir en mètres
            calepinageInfo.longueursMatieres.dessus = longueurNecessaire;
          }
        }
      }
      
      // Calculer la longueur pour le dessous
      if (!article.materiaux.dessous.enStock) {
        const piecesFond = pieces.filter(p => p.type === 'fond' && p.matiere === article.materiaux.dessous.matiere);
        if (piecesFond.length > 0) {
          const matiereDessous = matieres.find(m => m.id === article.materiaux.dessous.matiere);
          if (matiereDessous) {
            const longueurNecessaire = piecesFond[0].width / 100; // Convertir en mètres
            calepinageInfo.longueursMatieres.dessous = longueurNecessaire;
          }
        }
      }
      
      // Calculer la longueur pour les côtés
      if (!article.materiaux.cotes.enStock) {
        const piecesBandeau = pieces.filter(p => 
          (p.type === 'bandeau' || p.type === 'joue1' || p.type === 'joue2' || p.type === 'cylindre') && 
          p.matiere === article.materiaux.cotes.matiere
        );
        
        if (piecesBandeau.length > 0) {
          const matiereCotes = matieres.find(m => m.id === article.materiaux.cotes.matiere);
          if (matiereCotes) {
            let longueurTotale = 0;
            for (const piece of piecesBandeau) {
              longueurTotale += piece.width / 100; // Convertir en mètres
            }
            calepinageInfo.longueursMatieres.cotes = longueurTotale;
          }
        }
      }
    }
    
    return {
      prix,
      details: {
        dimensions: dimensionsCompletes,
        temps,
        surfaces: dimensionsCompletes.surfaces,
        coutures: dimensionsCompletes.coutures,
        accessoires: {
          fermeture: article.options.fermeture,
          mousse: article.options.mousse,
          volumeMousse: accessoires.volumeMousse
        },
        couts: {
          main_oeuvre: coutMainOeuvre,
          matieres: coutMatieres,
          accessoires: accessoires.cout,
          total: coutTotal
        },
        calepinage: calepinageInfo
      }
    };
  }
  
  /**
   * Convertit un article en pièces pour le calepinage
   */
  static articleEnPieces(article: Article): Piece[] {
    const pieces: Piece[] = [];

    // Vérifier si l'article a une forme définie
    const forme = article.forme || article.dimensions.forme || 'rectangulaire_bandeau';

    // Créer une copie des dimensions avec la forme explicitement définie
    const dimensionsAvecForme = {
      ...article.dimensions,
      forme: forme
    };
    
    // Calculer les dimensions avec talon
    const dimensionsAvecTalon = this.calculerDimensionsAvecTalon(dimensionsAvecForme);
    
    // S'assurer que avecTalon existe
    if (!dimensionsAvecTalon.avecTalon) {
      dimensionsAvecTalon.avecTalon = {};
    }
    
    // Calculer les surfaces
    const surfaces = this.calculerSurfaces(dimensionsAvecForme);
    
    // Calculer les coutures
    const coutures = this.calculerCoutures(dimensionsAvecForme);
    
    // Compléter les dimensions
    const dimensionsCompletes = {
      ...dimensionsAvecForme,
      avecTalon: dimensionsAvecTalon.avecTalon,
      surfaces,
      coutures
    };
    
    // S'assurer que avecTalon est défini
    if (!dimensionsCompletes.avecTalon) {
      dimensionsCompletes.avecTalon = {};
    }
    
    // Utiliser la forme définie précédemment
    
    if (forme === 'rectangulaire_bandeau') {
      // Dessus
      if (dimensionsCompletes.surfaces.dessus > 0 && dimensionsCompletes.avecTalon.longueur && dimensionsCompletes.avecTalon.largeur) {
        pieces.push({
          id: `${article.id}-dessus`,
          articleId: article.id,
          type: 'dessus',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessus.matiere,
          couleur: article.materiaux.dessus.couleur,
          surface: dimensionsCompletes.surfaces.dessus,
          width: dimensionsCompletes.avecTalon.longueur,
          height: dimensionsCompletes.avecTalon.largeur
        });
      }
      
      // Fond (si option fond)
      if (article.options.fond && dimensionsCompletes.surfaces.fond > 0 && dimensionsCompletes.avecTalon.longueur && dimensionsCompletes.avecTalon.largeur) {
        pieces.push({
          id: `${article.id}-fond`,
          articleId: article.id,
          type: 'fond',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessous.matiere,
          couleur: article.materiaux.dessous.couleur,
          surface: dimensionsCompletes.surfaces.fond,
          width: dimensionsCompletes.avecTalon.longueur,
          height: dimensionsCompletes.avecTalon.largeur
        });
      }
      
      // Bandeau
      if (dimensionsCompletes.surfaces.bandeau > 0 && dimensionsCompletes.avecTalon.perimetre && dimensionsCompletes.avecTalon.hauteur) {
        pieces.push({
          id: `${article.id}-bandeau`,
          articleId: article.id,
          type: 'bandeau',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.cotes.matiere,
          couleur: article.materiaux.cotes.couleur,
          surface: dimensionsCompletes.surfaces.bandeau,
          width: dimensionsCompletes.avecTalon.perimetre,
          height: dimensionsCompletes.avecTalon.hauteur
        });
      }
    } else if (forme === 'rectangulaire_joues') {
      // Bandeau plateau (utilise le type 'dessus' pour compatibilité)
      if (dimensionsCompletes.surfaces.dessus > 0 && dimensionsCompletes.avecTalon.longueur && dimensionsCompletes.avecTalon.largeur) {
        // Créer une copie des dimensions avec une propriété pour indiquer qu'il s'agit d'un bandeau-plateau
        const dimensionsAvecDescription = {
          ...dimensionsCompletes,
          description: 'bandeau-plateau' // Ajouter une description pour l'affichage
        };
        
        pieces.push({
          id: `${article.id}-bandeau-plateau`,
          articleId: article.id,
          type: 'dessus', // Utiliser 'dessus' comme type pour compatibilité
          dimensions: dimensionsAvecDescription,
          matiere: article.materiaux.dessus.matiere,
          couleur: article.materiaux.dessus.couleur,
          surface: dimensionsCompletes.surfaces.dessus,
          width: dimensionsCompletes.avecTalon.longueur,
          height: dimensionsCompletes.avecTalon.largeur
        });
      }
      
      // Fond (si option fond)
      if (article.options.fond && dimensionsCompletes.surfaces.fond > 0 && dimensionsCompletes.avecTalon.longueur && dimensionsCompletes.avecTalon.largeur) {
        pieces.push({
          id: `${article.id}-fond`,
          articleId: article.id,
          type: 'fond',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessous.matiere,
          couleur: article.materiaux.dessous.couleur,
          surface: dimensionsCompletes.surfaces.fond,
          width: dimensionsCompletes.avecTalon.longueur,
          height: dimensionsCompletes.avecTalon.largeur
        });
      }
      
      // Joues
      if (dimensionsCompletes.surfaces.bandeau > 0 && dimensionsCompletes.avecTalon.hauteur && dimensionsCompletes.avecTalon.largeur) {
        // Joue 1
        pieces.push({
          id: `${article.id}-joue1`,
          articleId: article.id,
          type: 'joue1',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.cotes.matiere,
          couleur: article.materiaux.cotes.couleur,
          surface: dimensionsCompletes.surfaces.bandeau / 2,
          width: dimensionsCompletes.avecTalon.hauteur,
          height: dimensionsCompletes.avecTalon.largeur
        });
        
        // Joue 2
        pieces.push({
          id: `${article.id}-joue2`,
          articleId: article.id,
          type: 'joue2',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.cotes.matiere,
          couleur: article.materiaux.cotes.couleur,
          surface: dimensionsCompletes.surfaces.bandeau / 2,
          width: dimensionsCompletes.avecTalon.hauteur,
          height: dimensionsCompletes.avecTalon.largeur
        });
      }
    } else if (forme === 'rond_bandeau') {
      // Dessus - Toujours ajouter le dessus, même si la surface est 0
      if (dimensionsCompletes.avecTalon.diametre) {
        pieces.push({
          id: `${article.id}-dessus`,
          articleId: article.id,
          type: 'dessus',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessus.matiere,
          couleur: article.materiaux.dessus.couleur,
          surface: dimensionsCompletes.surfaces.dessus || 0,
          width: dimensionsCompletes.avecTalon.diametre,
          height: dimensionsCompletes.avecTalon.diametre
          // Note: Les pièces circulaires sont traitées comme des carrés pour le calepinage
        });
      }
      
      // Fond (si option fond) - Toujours ajouter le fond si l'option est activée
      if (article.options.fond && dimensionsCompletes.avecTalon.diametre) {
        pieces.push({
          id: `${article.id}-fond`,
          articleId: article.id,
          type: 'fond',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessous.matiere,
          couleur: article.materiaux.dessous.couleur,
          surface: dimensionsCompletes.surfaces.fond || 0,
          width: dimensionsCompletes.avecTalon.diametre,
          height: dimensionsCompletes.avecTalon.diametre
          // Note: Les pièces circulaires sont traitées comme des carrés pour le calepinage
        });
      }
      
      // Bandeau - Calculer correctement les dimensions du bandeau
      if (dimensionsCompletes.avecTalon.diametre && dimensionsCompletes.avecTalon.hauteur) {
        // Calculer le périmètre du cercle
        const perimetre = Math.PI * dimensionsCompletes.avecTalon.diametre;
        
        pieces.push({
          id: `${article.id}-bandeau`,
          articleId: article.id,
          type: 'bandeau',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.cotes.matiere,
          couleur: article.materiaux.cotes.couleur,
          surface: dimensionsCompletes.surfaces.bandeau || 0,
          width: perimetre,
          height: dimensionsCompletes.avecTalon.hauteur
        });
      }
    } else if (forme === 'cylindre_joues') {
      // Joues (dessus) - Toujours ajouter les joues, même si la surface est 0
      if (dimensionsCompletes.avecTalon.diametre) {
        // Joue 1
        pieces.push({
          id: `${article.id}-joue1`,
          articleId: article.id,
          type: 'joue1',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessus.matiere,
          couleur: article.materiaux.dessus.couleur,
          surface: dimensionsCompletes.surfaces.dessus / 2 || 0,
          width: dimensionsCompletes.avecTalon.diametre,
          height: dimensionsCompletes.avecTalon.diametre,
          isCircular: true, // Indiquer que c'est une pièce circulaire
          formeCarreePourCalepinage: true // Indiquer qu'elle doit être traitée comme un carré pour le calepinage
        });
        
        // Joue 2
        pieces.push({
          id: `${article.id}-joue2`,
          articleId: article.id,
          type: 'joue2',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.dessous.matiere,
          couleur: article.materiaux.dessous.couleur,
          surface: dimensionsCompletes.surfaces.dessus / 2 || 0,
          width: dimensionsCompletes.avecTalon.diametre,
          height: dimensionsCompletes.avecTalon.diametre,
          isCircular: true, // Indiquer que c'est une pièce circulaire
          formeCarreePourCalepinage: true // Indiquer qu'elle doit être traitée comme un carré pour le calepinage
        });
      }
      
      // Cylindre - Calculer correctement les dimensions du cylindre
      if (dimensionsCompletes.avecTalon.diametre && article.dimensions.longueur) {
        // Calculer le périmètre du cercle
        const perimetre = Math.PI * dimensionsCompletes.avecTalon.diametre;
        const longueurAvecTalon = article.dimensions.longueur + 2 * (article.dimensions.talon || 0);
        
        pieces.push({
          id: `${article.id}-cylindre`,
          articleId: article.id,
          type: 'cylindre',
          dimensions: dimensionsCompletes,
          matiere: article.materiaux.cotes.matiere,
          couleur: article.materiaux.cotes.couleur,
          surface: dimensionsCompletes.surfaces.bandeau || 0,
          width: perimetre,
          height: longueurAvecTalon
        });
      }
    }
    
    return pieces;
  }
}
